import { useMemo, Fragment } from 'react';
import AppLayout from '../components/AppLayout';
import { StatChips } from '../components/HeaderControls';
import { useSettings } from '../hooks/useSettings';
import { useLedgerData } from '../hooks/useLedgerData';
import { fmtMoney } from '../utils/currency';
import { trialBalance, TYPE_LABEL } from '../utils/ledgerReports';
import type { LedgerAccountType } from '../types';

const TYPE_ORDER: LedgerAccountType[] = ['asset', 'liability', 'equity', 'income', 'expense'];

export default function TrialBalancePage() {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const { entries, accounts, loading } = useLedgerData();

  const tb = useMemo(() => trialBalance(entries, accounts), [entries, accounts]);
  const grouped = useMemo(() => {
    const by: Record<LedgerAccountType, typeof tb.rows> = { asset: [], liability: [], equity: [], income: [], expense: [] };
    tb.rows.forEach(r => by[r.type]?.push(r));
    return by;
  }, [tb]);

  return (
    <AppLayout title="Trial Balance" subtitle="Every account's balance — the books balance when Debits equal Credits">
      {loading ? (
        <div className="card"><div className="skeleton" style={{ height: 260, borderRadius: 8 }} /></div>
      ) : tb.rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>Nothing recorded yet</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
            Record purchases, sales and payments — or run <strong>Settings → Accounting → Rebuild Now</strong>.
          </div>
        </div>
      ) : (
        <>
          <StatChips items={[
            { label: 'Debits', value: money(tb.totalDebit) },
            { label: 'Credits', value: money(tb.totalCredit) },
            tb.balanced
              ? { label: 'Books', value: 'Balanced', tone: 'success' }
              : { label: 'Out of balance', value: `${money(Math.abs(tb.totalDebit - tb.totalCredit))}, please report this`, tone: 'danger' },
          ]} />
          <div className="card" style={{ padding: 0 }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Code</th>
                    <th>Account</th>
                    <th style={{ textAlign: 'right', width: 150 }}>Debit</th>
                    <th style={{ textAlign: 'right', width: 150 }}>Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {TYPE_ORDER.filter(t => grouped[t].length > 0).map(t => (
                    <Fragment key={t}>
                      <tr>
                        <td colSpan={4} style={{
                          fontSize: 11, fontWeight: 700, color: 'var(--text-3)',
                          textTransform: 'uppercase', letterSpacing: '0.06em', background: 'var(--surface-2)',
                        }}>{TYPE_LABEL[t]}</td>
                      </tr>
                      {grouped[t].map(r => (
                        <tr key={r.accountId}>
                          <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{r.code || '—'}</td>
                          <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.name}</td>
                          <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)', textAlign: 'right' }}>
                            {r.debit ? money(r.debit) : ''}
                          </td>
                          <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)', textAlign: 'right' }}>
                            {r.credit ? money(r.credit) : ''}
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: 'var(--surface-2)', borderTop: '2px solid var(--border)' }}>
                    <td colSpan={2} style={{ fontWeight: 700, fontSize: 12 }}>Total</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, fontSize: 12 }}>{money(tb.totalDebit)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, fontSize: 12 }}>{money(tb.totalCredit)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </AppLayout>
  );
}
